import {markRaw} from 'vue';
import {getURL} from '../CloudStorageService.js';
import {gmAPI} from '../MapService';
import {Graphic} from './Graphic.js';
import {mapsAddListenerMixin} from '@/lib/classes/EventHandling.js';

/**
 * @typedef {object} KMLData
 * @property {string} [imagePath] A path leading to a KML file in Google Cloud Storage.
 * @property {string} [url] A resolved URL leading to the KML file in Google Cloud Storage
 */

/** @typedef {import('./GraphicService').GraphicData & KMLData} KMLGraphicData */
/** @typedef {KMLGraphicData & {url: string}} ResolvedKMLGraphicData */

/**
 * @private
 * @type {google.maps.KmlLayerOptions}
 */
const DEFAULT_OPTIONS = {
  clickable: true,
  preserveViewport: true,
  suppressInfoWindows: true,
  map: null,
};

/**
 * Asynchronously constructs a KMLGraphic object.
 * @async
 * @param {KMLGraphicData} data The data from which to construct the graphic.
 * @return {KMLGraphic} The constructed graphic object.
 */
async function createKMLGraphic(data) {
  const url = data.url ?? await getURL(data.imagePath);
  return new KMLGraphic({...data, url});
}

class KMLGraphic extends Graphic {
  /**
   * @param {ResolvedKMLGraphicData} data
   */
  constructor(data) {
    super();
    /** @type {boolean} */
    this.clickable = DEFAULT_OPTIONS.clickable;
    /** @type {google.maps.KmlLayer} */
    this.kmlLayer = markRaw(new gmAPI.KmlLayer({
      ...DEFAULT_OPTIONS,
      url: data.url,
    }));
  }

  /**
   * Sets whether this graphic can be clicked.
   * @param {boolean} clickable If `true`, this overlay will be clickable on the map; otherwise
   * it will not be clickable.
   */
  setClickable(clickable) {
    this.clickable = clickable;
    this.kmlLayer.setOptions({clickable});
  }

  isClickable() {
    return this.clickable;
  }

  /**
   * Attaches the graphic to a map or removes it from a map.
   * @param {google.maps.Map|null} map The map to attach this graphic to,
   * or `null` to detach the graphic from its current map.
   */
  setMap(map) {
    this.kmlLayer.setMap(map);
  }
}

Object.assign(KMLGraphic.prototype, mapsAddListenerMixin('kmlLayer'));

export {createKMLGraphic, KMLGraphic};
